import { useReactiveVar } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementMetadataInput } from '@deltek/specification-client/core/Models/projectElementInput'
import { BasicDataNode, EventDataNode } from 'antd/es/tree'
import {
    setFamilyProjectElementMetadata,
    setProjectElementMetadata,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import React from 'react'
import { STATUS } from 'shared/utils/dropdownUtils'
import { getStatePUCPropertyValue } from 'shared/utils/workspaceUtils'
import { useProjectPropertiesStore } from '../ProjectPropertiesStore'
import { DataNode } from '../types/types'
export type TreeEventDataNode = EventDataNode<BasicDataNode | DataNode>

const DEFAULT_LOS_LEVEL = 100

export const useProjectPropertiesHandler = (
    updateProjectProperties: (
        finalSelectedValues: ProjectElementMetadataInput,
        projectElementNode: ProjectElementNode | null
    ) => void
) => {
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const assignedUsersList = useProjectPropertiesStore(
        (s) => s.assignedUsersList
    )
    const defaultSelectedValues: ProjectElementMetadataInput = {
        assignedUserId: null,
        state: 'NEW',
        currentLOD: DEFAULT_LOS_LEVEL,
        targetLOD: DEFAULT_LOS_LEVEL,
    }
    const [currentSelectedValues, setCurrentSelectedValues] = React.useState<
        ProjectElementMetadataInput
    >(defaultSelectedValues)
    const {
        projectMetadata: projectElementMetadata,
        familyProjectMetadata,
    } = useReactiveVar(workspaceSelectedElement)

    const isFamilyElement = React.useMemo(
        () => selectedProjectElementNode?.elementType === 'PRODUCTFAMILY',
        [selectedProjectElementNode]
    )

    const handleChange = (paramName: string, paramValue: string | null) => {
        let finalValue: string | number | null = paramValue
        if (paramName === 'assignedUserId') {
            finalValue =
                assignedUsersList.find((user) => user.userName === paramValue)
                    ?.userId ?? defaultSelectedValues.assignedUserId
        } else if (paramName === 'currentLOD' || paramName === 'targetLOD') {
            if (paramValue) {
                finalValue = parseInt(paramValue, 10)
            } else {
                finalValue =
                    paramName === 'currentLOD'
                        ? defaultSelectedValues.currentLOD
                        : defaultSelectedValues.targetLOD
            }
        } else if (paramName === 'state') {
            if (!paramValue) {
                finalValue = defaultSelectedValues.state
            } else {
                finalValue = STATUS.filter(
                    (status) =>
                        status.value === getStatePUCPropertyValue(paramValue)
                )[0].value
            }
        }
        const finalSelectedValues: ProjectElementMetadataInput = {
            ...currentSelectedValues,
            [paramName]: finalValue,
        }

        updateProjectProperties(finalSelectedValues, selectedProjectElementNode)
        setCurrentSelectedValues(finalSelectedValues)
        updateProjectElementMetaDataState(finalSelectedValues)
    }

    const updateProjectElementMetaDataState = (
        selectedValues: ProjectElementMetadataInput
    ) => {
        const assignedUserName = assignedUsersList.find(
            (user) => user.userId === selectedValues.assignedUserId
        )?.userName

        const stateName = STATUS.filter(
            (status) => status.value === selectedValues.state
        )[0].name

        setProjectElementMetadata({
            ...projectElementMetadata,
            assignedUserId:
                selectedValues.assignedUserId ??
                defaultSelectedValues.assignedUserId,
            assignedUser: assignedUserName,
            currentLOD:
                selectedValues.currentLOD ?? defaultSelectedValues.currentLOD,
            targetLOD:
                selectedValues.targetLOD ?? defaultSelectedValues.targetLOD,
            state: stateName ?? defaultSelectedValues.state,
        })

        if (isFamilyElement) {
            setFamilyProjectElementMetadata({
                ...familyProjectMetadata,
                assignedUserId:
                    selectedValues.assignedUserId ??
                    defaultSelectedValues.assignedUserId,
                assignedUser: assignedUserName,
                currentLOD:
                    selectedValues.currentLOD ??
                    defaultSelectedValues.currentLOD,
                targetLOD:
                    selectedValues.targetLOD ?? defaultSelectedValues.targetLOD,
                state: stateName ?? defaultSelectedValues.state,
                isTargetLevelOfSpecificationManuallyUpdated: true,
            })
        }
    }

    const initializeSelectedValues = () => {
        if (selectedProjectElementNode) {
            if (
                projectElementMetadata &&
                selectedProjectElementNode.elementId
            ) {
                const selectedValues: ProjectElementMetadataInput = {
                    assignedUserId:
                        projectElementMetadata?.assignedUserId ??
                        defaultSelectedValues.assignedUserId,
                    state:
                        getStatePUCPropertyValue(
                            projectElementMetadata?.state
                        ) ?? defaultSelectedValues.state,
                    currentLOD:
                        projectElementMetadata?.currentLOD ??
                        defaultSelectedValues.currentLOD,
                    targetLOD:
                        projectElementMetadata?.targetLOD ??
                        defaultSelectedValues.targetLOD,
                }

                setCurrentSelectedValues(selectedValues)
            } else {
                setCurrentSelectedValues(defaultSelectedValues)
            }
        }
    }

    return {
        currentSelectedValues,
        setCurrentSelectedValues,
        handleChange,
        initializeSelectedValues,
    }
}
